import React, { useEffect, useMemo, useState } from "react";

import { CheckCircle2, Clock, Mail, Pencil, UserPlus } from "lucide-react";

import SendOnboardingInviteModal from "./SendOnboardingInviteModal";
import SendReminderModal from "./SendReminderModal";
import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import PageHeader from "../../Components/PageHeader";
import SearchInput from "../../Components/Search/SearchInput";
import Table from "../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  EasyOnboardingSettings,
  fetchEasyOnboardingConfig,
  fetchOnboardingInvitations,
  OnboardingInvitation,
  sendInvitationReminder,
  clearRemindError,
  updateEasyOnboardingConfig,
} from "../../Store/Reducers/EasyOnboardingSlice";
import { fetchVendorAdminGrades } from "../../Store/Reducers/VendorAdminSlice";
import {
  isOnboardingCLevelHint,
  isOnboardingExecutiveHint,
} from "../../Utils/Constants/RoleConstants";
import { formatDateUS } from "../../Utils/Helper/Helper";
import { toast } from "../../Utils/Helper/toast";

const formatOnboardingDate = (value?: string | null) => {
  if (!value) return "—";
  const formatted = formatDateUS(value).split(",")[0];
  return formatted === "-" ? "—" : formatted;
};

const getInitials = (name?: string | null) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
};

const avatarColors = [
  "bg-purple-100 text-purple-700 ring-purple-200",
  "bg-indigo-100 text-indigo-700 ring-indigo-200",
  "bg-violet-100 text-violet-700 ring-violet-200",
  "bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200",
];

const getAvatarColor = (seed: string) => {
  const hash = seed
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return avatarColors[hash % avatarColors.length];
};

const StatusBadge: React.FC<{
  status: string;
  acceptedAt?: string | null;
}> = ({ status, acceptedAt }) => {
  const normalized = status.toLowerCase();

  if (normalized === "accepted") {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
          <CheckCircle2 className="h-3 w-3" />
          Accepted
        </span>
        {acceptedAt ? (
          <span className="text-xs text-gray-500 pl-0.5">
            {formatOnboardingDate(acceptedAt)}
          </span>
        ) : null}
      </div>
    );
  }

  if (normalized === "pending") {
    return (
      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-200">
        <Clock className="h-3 w-3" />
        Pending
      </span>
    );
  }

  if (normalized === "expired") {
    return (
      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
        <Clock className="h-3 w-3" />
        Expired
      </span>
    );
  }

  if (normalized === "cancelled") {
    return (
      <span className="inline-flex w-fit items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 ring-1 ring-gray-200">
        Cancelled
      </span>
    );
  }

  return (
    <span className="inline-flex w-fit items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 capitalize">
      {status}
    </span>
  );
};

const RoleBadge: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-indigo-100">
    {label}
  </span>
);

const getInvitedAsLabel = (record: {
  company_identity_hint?: string | null;
  team_role_hint?: string | null;
  program_role_hint?: string | null;
}) => {
  if (isOnboardingCLevelHint(record.company_identity_hint)) {
    return "C-Level";
  }
  if (isOnboardingExecutiveHint(record.company_identity_hint)) {
    return "Executive";
  }
  return record.team_role_hint || record.program_role_hint || "—";
};

const EasyOnboarding: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    config,
    configKey,
    configLoading,
    configUpdateLoading,
    invitations,
    invitationsLoading,
    remindLoading,
    remindError,
  } = useAppSelector(state => state.easyOnboarding);
  const { grades } = useAppSelector(state => state.vendorAdmin);

  const [searchQuery, setSearchQuery] = useState("");
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [tempSettings, setTempSettings] =
    useState<EasyOnboardingSettings>(config);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [reminderTarget, setReminderTarget] =
    useState<OnboardingInvitation | null>(null);

  const canSendReminder = (status: string) => {
    const normalized = status.toLowerCase();
    return normalized === "pending" || normalized === "expired";
  };

  const handleSendReminder = async () => {
    if (!reminderTarget) return;

    try {
      await dispatch(sendInvitationReminder(reminderTarget.id)).unwrap();
      setReminderTarget(null);
    } catch {
      // Error toast handled in slice
    }
  };

  useEffect(() => {
    dispatch(fetchEasyOnboardingConfig());
    dispatch(fetchOnboardingInvitations());
    dispatch(fetchVendorAdminGrades());
  }, [dispatch]);

  useEffect(() => {
    if (!isEditingSettings) {
      setTempSettings(config);
    }
  }, [config, isEditingSettings]);

  const gradeLabelByValue = useMemo(() => {
    const items = grades?.items ?? [];
    const map = new Map<number, string>();
    items.forEach((item: { grade: number; grade_name: string }) => {
      map.set(Number(item.grade), item.grade_name || `Grade ${item.grade}`);
    });
    return map;
  }, [grades]);

  const filteredInvitations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return invitations;

    return invitations.filter(invite => {
      const name = (invite.invited_name || "").toLowerCase();
      const email = (invite.invited_email || "").toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [invitations, searchQuery]);

  const handleEditSettings = () => {
    setTempSettings({ ...config });
    setIsEditingSettings(true);
  };

  const handleCancelSettings = () => {
    setTempSettings({ ...config });
    setIsEditingSettings(false);
  };

  const handleSaveSettings = async () => {
    if (!configKey) {
      toast.error("Onboarding settings are not loaded yet.");
      return;
    }

    try {
      await dispatch(
        updateEasyOnboardingConfig({
          key: configKey,
          value: tempSettings,
        })
      ).unwrap();
      setIsEditingSettings(false);
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save onboarding settings."
      );
    }
  };

  const handleSettingChange = (
    field: keyof EasyOnboardingSettings,
    value: string
  ) => {
    const numValue = Math.max(1, parseInt(value, 10) || 1);
    setTempSettings(prev => ({
      ...prev,
      [field]: numValue,
    }));
  };

  const tableColumns = [
    {
      key: "invited_name",
      title: "Name",
      width: "220px",
      render: (_: unknown, record: (typeof invitations)[number]) => {
        const name = record.invited_name || "—";
        const email = record.invited_email || "";
        const seed = email || name;
        return (
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-1 ${getAvatarColor(seed)}`}
            >
              {getInitials(record.invited_name)}
            </span>
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">{name}</p>
              {email ? (
                <p className="text-xs text-gray-500 truncate">{email}</p>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      key: "invited_by_name",
      title: "Invited by",
      width: "140px",
      render: (value: string | null) => (
        <span className="text-gray-700">{value || "—"}</span>
      ),
    },
    {
      key: "invited_as",
      title: "Invited as",
      width: "110px",
      render: (_: unknown, record: (typeof invitations)[number]) => (
        <RoleBadge label={getInvitedAsLabel(record)} />
      ),
    },
    {
      key: "invited_grade",
      title: "Grade",
      width: "100px",
      render: (value: number | null | undefined) => {
        if (!value) {
          return <span className="text-gray-400">—</span>;
        }
        const label = gradeLabelByValue.get(value) || `Grade ${value}`;
        return (
          <span className="inline-flex items-center rounded-md bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-violet-100">
            {label}
          </span>
        );
      },
    },
    {
      key: "status",
      title: "Status",
      width: "130px",
      render: (_: unknown, record: (typeof invitations)[number]) => (
        <StatusBadge status={record.status} acceptedAt={record.accepted_at} />
      ),
    },
    {
      key: "claimed_team_role_hint",
      title: "Claimed as",
      width: "110px",
      render: (value: string | null | undefined) =>
        value ? (
          <RoleBadge label={value} />
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "token_expires_at",
      title: "Expires",
      width: "110px",
      render: (value: string) => (
        <span className="text-sm text-gray-600 tabular-nums">
          {formatOnboardingDate(value)}
        </span>
      ),
    },
    {
      key: "reminded_count",
      title: "Reminders",
      width: "100px",
      textalign: "center" as const,
      render: (value: number | null | undefined) => {
        const count = value ?? 0;
        return (
          <span
            className={`inline-flex min-w-[1.75rem] items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
              count > 0
                ? "bg-purple-100 text-purple-700 ring-1 ring-purple-200"
                : "bg-gray-50 text-gray-500 ring-1 ring-gray-200"
            }`}
          >
            {count}
          </span>
        );
      },
    },
    {
      key: "action",
      title: "Action",
      width: "80px",
      textalign: "center" as const,
      render: (_: unknown, record: (typeof invitations)[number]) => {
        const remindable = canSendReminder(record.status);
        return (
          <button
            type="button"
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
              remindable
                ? "bg-purple-50 text-purple-600 ring-1 ring-purple-200 hover:bg-purple-100 hover:text-purple-700"
                : "cursor-not-allowed bg-gray-50 text-gray-300"
            }`}
            disabled={!remindable}
            title={
              remindable
                ? "Send reminder"
                : "Reminders are only available for pending or expired invites"
            }
            onClick={() => {
              if (remindable) {
                dispatch(clearRemindError());
                setReminderTarget(record);
              }
            }}
          >
            <Mail size={16} />
          </button>
        );
      },
    },
  ];

  return (
    <div className="bg-white overflow-hidden mt-3">
      <PageHeader
        title="Easy Onboarding"
        icon={UserPlus}
        subTitle="Configure settings, send user invitations, track onboarding progress, and send reminders."
        actions={[
          <Button
            key="send-invite"
            variant="primary"
            icon={<UserPlus size={16} />}
            onClick={() => setIsInviteModalOpen(true)}
          >
            Invite User
          </Button>,
        ]}
      />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Onboarding settings
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configure invite validity and reminder limits for easy
                  onboarding.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-6 items-start">
                <CommonInput
                  label="Invite expiry (hours)"
                  name="invite_expiry_hours"
                  type="number"
                  min="1"
                  value={String(
                    isEditingSettings
                      ? tempSettings.invite_expiry_hours
                      : config.invite_expiry_hours
                  )}
                  onChange={e =>
                    handleSettingChange("invite_expiry_hours", e.target.value)
                  }
                  disabled={!isEditingSettings || configLoading}
                  infoText="Time window before an invite link expires."
                />

                <CommonInput
                  label="Reminder cooldown (hours)"
                  name="reminder_cooldown_hours"
                  type="number"
                  min="1"
                  value={String(
                    isEditingSettings
                      ? tempSettings.reminder_cooldown_hours
                      : config.reminder_cooldown_hours
                  )}
                  onChange={e =>
                    handleSettingChange(
                      "reminder_cooldown_hours",
                      e.target.value
                    )
                  }
                  disabled={!isEditingSettings || configLoading}
                  infoText="Minimum wait time before sending another reminder for the same invite."
                />

                <CommonInput
                  label="Max invites per day"
                  name="max_invites_per_day"
                  type="number"
                  min="1"
                  value={String(
                    isEditingSettings
                      ? tempSettings.max_invites_per_day
                      : config.max_invites_per_day
                  )}
                  onChange={e =>
                    handleSettingChange("max_invites_per_day", e.target.value)
                  }
                  disabled={!isEditingSettings || configLoading}
                  infoText="Caps new onboarding invites created per calendar day. Resending a pending invite to the same email does not use another slot."
                />

                <div className="flex gap-2">
                  {!isEditingSettings ? (
                    <Button
                      variant="primary"
                      icon={<Pencil size={16} />}
                      onClick={handleEditSettings}
                      disabled={configLoading}
                    >
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        onClick={handleCancelSettings}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSaveSettings}
                        loading={configUpdateLoading}
                      >
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Team tracker
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Track onboarding invitations and send reminders.
                  </p>
                </div>
                <div className="w-full md:w-80">
                  <SearchInput
                    placeholder="Search name or email..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    rounded="normal"
                  />
                </div>
              </div>

              <Table
                columns={tableColumns}
                data={filteredInvitations}
                loading={invitationsLoading}
                pagination
                defaultRowsPerPage={10}
                rowKey={record => record.id}
              />
            </div>
          </div>
        </div>
      </div>

      <SendOnboardingInviteModal
        isOpen={isInviteModalOpen}
        toggle={() => setIsInviteModalOpen(false)}
      />

      <SendReminderModal
        isOpen={!!reminderTarget}
        toggle={() => {
          dispatch(clearRemindError());
          setReminderTarget(null);
        }}
        invitedEmail={reminderTarget?.invited_email || ""}
        onConfirm={handleSendReminder}
        loading={remindLoading}
        error={remindError}
      />
    </div>
  );
};

export default EasyOnboarding;
